
import React, { useEffect, useRef } from 'react';

export default function StarField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;
    let animationFrameId: number;
    
    // Star properties
    const stars: { x: number; y: number; radius: number; twinkleSpeed: number; offset: number; driftX: number; driftY: number }[] = [];
    const numStars = 150; // Dense enough to look good, sparse enough for performance

    const initStars = () => {
      stars.length = 0;
      for (let i = 0; i < numStars; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          radius: Math.random() * 1.5 + 0.3, // Varied sizes
          twinkleSpeed: Math.random() * 0.03 + 0.005, // Varied twinkle speeds
          offset: Math.random() * Math.PI * 2, // Random starting phase
          driftX: (Math.random() - 0.5) * 0.05, // Very slow horizontal drift
          driftY: (Math.random() - 0.5) * 0.05 - 0.05, // Slight upward bias for "rising" feeling
        });
      }
    };

    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initStars();
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const render = () => {
      ctx.clearRect(0, 0, width, height);
      
      stars.forEach((star) => {
        // Twinkle logic: Sine wave creates smooth fading
        star.offset += star.twinkleSpeed;
        const opacity = Math.abs(Math.sin(star.offset)) * 0.4 + 0.2; // Opacity swings between 0.2 and 0.6
        
        // Parallax / Drift logic
        star.x += star.driftX;
        star.y += star.driftY;

        // Wrap around screen
        if (star.x < 0) star.x = width;
        if (star.x > width) star.x = 0;
        if (star.y < 0) star.y = height;
        if (star.y > height) star.y = 0;

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 z-0 pointer-events-none mix-blend-screen"
    />
  );
}
